import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Modal from './Modal';

const ToDoModal = ({ isOpen, onClose, todo, assetId, assetType = 'home', onSave }) => {
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const initialFormData = {
    title: '',
    description: '',
    expected_start_date: '',
    expected_end_date: '',
    expected_cost: 0,
    next_steps: '',
    expense_category: '',
    status: 'not_started',
    completed_date: ''
  };

  const [formData, setFormData] = useState(initialFormData);

  const statusChoices = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In-Progress' },
    { value: 'delayed_on_hold', label: 'Delayed/On-Hold' },
    { value: 'completed', label: 'Completed' }
  ];

  const reverseMapping = {
    'repair': 'home_repair',
    'maintenance': 'home_maintenance',
    'enhancement': 'home_enhancement',
    'taxes': 'home_tax_payment',
    'insurance': 'home_insurance_payment',
    'home_decor': 'home_decor',
    'home_furniture': 'home_furniture',
    'home_artwork': 'home_artwork',
    'pest_control': 'home_pest_control',
    'home_inspection': 'home_inspection',
    'home_other': 'home_other'
  };

  useEffect(() => {
    if (todo) {
      let displayCat = todo.expense_category;
      if (displayCat && reverseMapping[displayCat]) {
        displayCat = reverseMapping[displayCat];
      }

      setFormData({
        title: todo.title || '',
        description: todo.description || '',
        expected_start_date: todo.expected_start_date || '',
        expected_end_date: todo.expected_end_date || '',
        expected_cost: todo.expected_cost || 0,
        next_steps: todo.next_steps || '',
        expense_category: displayCat || '',
        status: todo.status || 'not_started',
        completed_date: todo.completed_date || ''
      });
    } else {
      setFormData(initialFormData);
    }
  }, [todo, isOpen]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/');
      let fetchedCategories = [];
      if (res.data && Array.isArray(res.data)) {
          fetchedCategories = res.data;
      } else if (res.data && res.data.items) {
          const flat = [];
          const walk = (items) => {
              items.forEach(item => {
                  flat.push(item);
                  if (item.children) walk(item.children);
              });
          }
          walk(res.data.items);
          fetchedCategories = flat;
      }

      if (assetType === 'vehicle') {
          setCategories(fetchedCategories.filter(c => 
            c.value === 'vehicle' || 
            c.value.startsWith('vehicle_') || 
            ['licensing_tags', 'repair', 'maintenance', 'enhancement', 'taxes', 'insurance', 'ticket_or_fine', 'asset_input', 'asset_storage', 'financing_interest', 'financing_principal'].includes(c.value)
          ));
      } else {
          setCategories(fetchedCategories.filter(c => 
            c.value === 'home' || 
            c.value.startsWith('home_') || 
            c.value === 'landscaping' ||
            c.value === 'plants' ||
            c.value === 'pest_control' ||
            ['repair', 'maintenance', 'enhancement', 'taxes', 'insurance', 'utilities', 'utilities_electric_gas', 'utilities_water', 'utilities_trash', 'utilities_sewer', 'utilities_internet', 'utilities_cable_tv', 'utilities_phone', 'utilities_other', 'financing_interest', 'financing_principal'].includes(c.value)
          ));
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, [assetType]);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen, fetchCategories]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dataToSend = { ...formData, asset: assetId || (todo ? todo.asset : null) };

      const categoryMapping = {
        'home_repair': 'repair',
        'home_maintenance': 'maintenance',
        'home_enhancement': 'enhancement',
        'home_decor': 'home_decor',
        'home_furniture': 'home_furniture',
        'home_artwork': 'home_artwork',
        'home_pest_control': 'pest_control',
        'home_inspection': 'home_inspection',
        'home_tax_payment': 'taxes',
        'home_insurance_payment': 'insurance',
        'home_other': 'home_other',
        'landscaping': 'landscaping',
        'plants': 'plants',
        'pest_control': 'pest_control',
        'vehicle_maintenance': 'vehicle_maintenance',
        'vehicle_repair': 'vehicle_repair',
        'vehicle_enhancement': 'vehicle_enhancement',
        'vehicle_other': 'vehicle_other',
        'licensing_tags': 'licensing_tags',
        'repair': 'repair',
        'maintenance': 'maintenance',
        'enhancement': 'enhancement'
      };

      if (dataToSend.expense_category && categoryMapping[dataToSend.expense_category]) {
        dataToSend.expense_category = categoryMapping[dataToSend.expense_category];
      }
      
      const optionalFields = [
        'expected_start_date', 
        'expected_end_date', 
        'expense_category', 
        'next_steps',
        'completed_date'
      ];
      
      optionalFields.forEach(field => {
        if (!dataToSend[field]) {
          delete dataToSend[field];
        }
      });
      
      if (dataToSend.expected_cost === '' || dataToSend.expected_cost === null || dataToSend.expected_cost === undefined) {
        dataToSend.expected_cost = 0;
      }
      
      let res;
      if (todo) {
        res = await axios.patch(`/api/balance_sheet/assets/physical_assets/todo-items/${todo.id}/`, dataToSend);
      } else {
        res = await axios.post(`/api/balance_sheet/assets/physical_assets/${dataToSend.asset}/todo-items/`, dataToSend);
      }
      
      if (onSave) onSave(res.data);
      onClose();
    } catch (err) {
      console.error('Error saving todo:', err);
      alert('Error saving to-do item. Please check mandatory fields.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={todo ? 'Edit To-Do Item' : 'New To-Do Item'} width={800}>
      <div style={{ background: '#111827', padding: '16px', borderRadius: '8px' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>Title *</label>
              <input name="title" value={formData.title} onChange={handleChange} required className="form-control form-control-sm" style={{ background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }} />
            </div>
            <div>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>Category</label>
              <select name="expense_category" value={formData.expense_category} onChange={handleChange} className="form-select form-select-sm" style={{ background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }}>
                <option value="">Select Category</option>
                {categories.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>Description *</label>
              <textarea name="description" value={formData.description} onChange={handleChange} required className="form-control form-control-sm" rows="2" style={{ background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }} />
            </div>
            <div>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>Expected Start</label>
              <input type="date" name="expected_start_date" value={formData.expected_start_date} onChange={handleChange} className="form-control form-control-sm" style={{ background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }} />
            </div>
            <div>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>Expected End</label>
              <input type="date" name="expected_end_date" value={formData.expected_end_date} onChange={handleChange} className="form-control form-control-sm" style={{ background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }} />
            </div>
            <div>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>Expected Cost</label>
              <input type="number" name="expected_cost" value={formData.expected_cost} onChange={handleChange} className="form-control form-control-sm" style={{ background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }} />
            </div>
            <div>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className="form-select form-select-sm" style={{ background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }}>
                {statusChoices.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>Completed Date</label>
              <input type="date" name="completed_date" value={formData.completed_date} onChange={handleChange} className="form-control form-control-sm" style={{ background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>Next Steps</label>
              <textarea name="next_steps" value={formData.next_steps} onChange={handleChange} className="form-control form-control-sm" rows="2" style={{ background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }} />
            </div>
          </div>
          <div style={{ marginTop: '16px', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn btn-primary">{saving ? 'Saving...' : (todo ? 'Update To-Do' : 'Save To-Do')}</button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ToDoModal;
